import Avatar from "@/components/Avatar.vue";
import NotificationWrapper from "@/components/v2/NotificationWrapper";
import GlobalError from "@/components/v2/GlobalError";
import GlobalSearch from "@/components/v2/GlobalSearch";
import PredictileLogo from "@/assets/images/current_color_logo_horizontal.svg";
import RxHarmonyLogo from "@/assets/images/rx-harmony-logo.svg";
import PredictileColorLogo from "@/assets/images/predictile_logo_rgb.svg";
import {
  AlertIcon,
  MailIcon,
  MenuIcon,
  XIcon,
  ChevronDownIcon,
  UserCircleIcon,
  UserIcon,
  DocumentIcon,
  SignOutIcon,
  BusinessIcon,
  UsersIcon,
  CombineIcon,
  TestTubeIcon,
  GridIcon,
  SupportIcon,
  GraphIcon,
  PillIcon,
  CheckIcon,
  TrainingIcon,
  TableIcon,
  ReportIcon,
  SettingsIcon,
  LocationIcon,
  KeyIcon,
  TaskListIcon,
} from "@/assets/svgs";
import { mapGetters, mapActions, mapState } from "vuex";
import redirect from "@/mixins/redirect";
import { capitalize } from "lodash";

export default {
  mixins: [redirect],
  components: {
    Avatar,
    AlertIcon,
    MailIcon,
    NotificationWrapper,
    GlobalError,
    GlobalSearch,
    PredictileLogo,
    RxHarmonyLogo,
    PredictileColorLogo,
    MenuIcon,
    XIcon,
    ChevronDownIcon,
    UserCircleIcon,
    UserIcon,
    DocumentIcon,
    SignOutIcon,
    BusinessIcon,
    UsersIcon,
    CombineIcon,
    TestTubeIcon,
    GridIcon,
    SupportIcon,
    GraphIcon,
    PillIcon,
    CheckIcon,
    TrainingIcon,
    TableIcon,
    ReportIcon,
    SettingsIcon,
    LocationIcon,
    KeyIcon,
    TaskListIcon,
  },
  data() {
    return {
      isSidebarOpen: false,
      todoCount: 0,
      isLoadingCount: false,
      businessHasPins: false,
      todoInterval: null,
    };
  },
  computed: {
    ...mapGetters("cognito", { userId: "id", userName: "name", userEmail: "email" }),
    ...mapGetters("organization", [
      "organization",
      "organizations",
      "isOrganizationPredictile",
      "isOrganizationBusiness",
      "isOrganizationUser",
      "hasMultipleOrganizations",
      "cobrandId",
    ]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered", "isPscSecondaryRole", "can", "isPermissionGoverned"]),
    ...mapGetters("location", ["location", "locations"]),
    ...mapGetters("businessActivation", [
      "hasBusinessProfile",
      "hasProductsAndPlans",
      "hasBrandColorsAndLogo",
      "hasTaxRates",
      "hasLocation",
      "hasSubscription",
      "hasMerchantProcessing",
    ]),
    ...mapState("cognito", ["isAuthenticatedInPinMode"]),
    cleanUserName() {
      if (this.isOrganizationUser) return this.organization.customerLabelSingular !== null ? capitalize(this.organization.customerLabelSingular) + " Profile" : "Member Profile";
      else return this.userName.replace("Branch Development: ", "");
    },
    showActivationHeader() {
      return this.isOrganizationBusiness && !this.organization.isActive;
    },
    numRemainingActivationSteps() {
      let numRemaining = 0;
      if (!this.hasBusinessProfile) numRemaining++;
      if (!this.hasProductsAndPlans) numRemaining++;
      if (!this.hasBrandColorsAndLogo) numRemaining++;
      if (!this.hasTaxRates) numRemaining++;
      if (!this.hasLocation) numRemaining++;
      if (!this.hasSubscription) numRemaining++;
      if (!this.hasMerchantProcessing) numRemaining++;
      return numRemaining;
    },
    profileOptions() {
      const options = [];
      if (this.hasMultipleOrganizations || this.isPredictile) {
        options.push(
          ...this.organizations
            .filter((org) => org.id !== this.organization.id)
            .map((org) => ({
              title: org.description,
              description: this.getOrganizationDescriptor(org),
              icon: this.getOrganizationIcon(org),
              onClick: () => this.updateOrganization(org.id),
            })),
        );
        options.push({ isDivider: true });
      }
      if (this.canEnablePinMode && this.can("dashboard.pin-mode")) {
        options.push(
          {
            title: "Enable Pin Mode",
            icon: "key-icon",
            onClick: this.enablePinMode,
          },
          {
            isDivider: true,
          },
        );
      }
      options.push(
        {
          title: "My Profile",
          icon: "user-icon",
          onClick: () => {
            const targetRoute = `/users/${this.userId}/profile/`;
            if (this.$route.path !== targetRoute) {
              this.$router.push(targetRoute);
            }
          },
        },
        { title: "Sign Out", icon: "sign-out-icon", onClick: () => this.$router.push(`/signout${this.cobrandId ? `?cobrand=${this.cobrandId}` : ""}`) },
      );

      return options;
    },
    locationOptions() {
      return [...this.locations, { name: "All locations", isAll: true }];
    },
    navItems() {
      if (this.isOrganizationPredictile) {
        return this.predictileLinks;
      } else if (this.isOrganizationBusiness) {
        return this.businessLinks;
      } else if (this.isOrganizationUser) {
        return this.userLinks;
      }

      return [];
    },
    predictileLinks() {
      return [
        { text: "Businesses", icon: "business-icon", path: "/businesses" },
        { text: "Users", icon: "users-icon", path: "/admin/users" },
        { text: "Integrations", icon: "combine-icon", path: "/admin/integrations" },
        { text: "Services", icon: "test-tube-icon", path: "/admin/services" },
        { text: "Email Templates", icon: "mail-icon", path: "/admin/email" },
        { text: "Reconcile", icon: "report-icon", path: "/admin/subscription-reconcile" },
      ];
    },
    businessLinks() {
      const isBusinessManagement = this.isPredictile() || this.isBusinessManagement(this.organization.id);
      const isBusinessAdministrator = this.isPredictile() || this.isBusinessAdministrator(this.organization.id);

      const matrixGoverned = this.isPermissionGoverned;
      const links = [
        { text: "Dashboard", icon: "graph-icon", path: `/businesses/${this.organization.id}/home`, cap: "home" },
        {
          text: capitalize(this.organization.customerLabelPlural),
          icon: "users-icon",
          path: `/businesses/${this.organization.id}/members`,
          cap: "members",
        },
        ...(this.organization.usePrescriptionProgramSetting === true
          ? [{ text: "Prescriptions", icon: "pill-icon", path: `/businesses/${this.organization.id}/prescriptions`, cap: "prescriptions" }]
          : []),
        ...(this.organization.useTodoSetting === true && this.isLocationFiltered === false
          ? [
              {
                text: "Activities",
                icon: "check-icon",
                path: `/businesses/${this.organization.id}/business-todo/unassigned`,
                cap: "business-todo",
              },
            ]
          : []),
        ...(matrixGoverned || (isBusinessAdministrator === true && this.isLocationFiltered === false)
          ? [
              {
                text: "Disputes",
                icon: "alert-icon",
                path: `/businesses/${this.organization.id}/disputes`,
                cap: "disputes",
              },
            ]
          : []),
        ...(this.organization.useTodoSetting === true && this.isLocationFiltered === true
          ? [
              {
                text: "Activities",
                icon: "check-icon",
                path: `/businesses/${this.organization.id}/location-todo/${this.cobrandId === "rx-harmony" ? "follow-up" : "due-today"}`,
                cap: "business-todo",
              },
            ]
          : []),
        { text: "Resources", icon: "training-icon", path: `/businesses/${this.organization.id}/trainings/mytraining`, cap: "trainings" },
        ...(matrixGoverned || !(!isBusinessManagement || this.isPscSecondaryRole || (this.isLocationFiltered && this.organization.operatingModel === "coordinator"))
          ? [{ text: "Plans & Products", icon: "table-icon", path: `/businesses/${this.organization.id}/offerings`, cap: "offerings" }]
          : []),
        ...(matrixGoverned || !this.isPscSecondaryRole ? [{ text: "Reports", icon: "report-icon", path: `/businesses/${this.organization.id}/reports`, cap: "reports" }] : []),
        { text: "Settings", icon: "settings-icon", path: `/businesses/${this.organization.id}/business`, cap: "business" },
        ...(matrixGoverned || !this.isPscSecondaryRole ? [{ text: "Support", icon: "support-icon", path: `/businesses/${this.organization.id}/support`, cap: "support" }] : []),
      ];
      return links.filter((link) => !link.cap || this.can(link.cap));
    },
    userLinks() {
      return [
        { text: "Home", shortText: "Home", icon: "grid-icon", path: `/users/${this.organization.id}/plans` },
        { text: "My Resources", shortText: "Resources", icon: "document-icon", path: `/users/${this.organization.id}/documents` },
        { text: "My Profile", shortText: "Profile", icon: "user-icon", path: `/users/${this.organization.id}/profile` },
        { text: "Support", icon: "support-icon", path: "/user/support" },
      ];
    },
    logo() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return "rx-harmony-logo";
      }
      return "predictile-logo";
    },
    canEnablePinMode() {
      return this.isOrganizationBusiness && this.businessHasPins;
    },
  },
  created() {
    this.loadTodoCount();

    setInterval(
      function () {
        this.loadTodoCount();
      }.bind(this),
      90000,
    );
    this.checkForBusinessPins();
    this.EventBus.$on("business-user-updated", this.checkForBusinessPins);
  },
  beforeUnmount() {
    this.EventBus.$off("business-user-updated", this.checkForBusinessPins);

    if (this.todoInterval) {
      clearInterval(this.todoInterval);
      this.todoInterval = null;
    }
  },

  watch: {
    organization() {
      this.checkForBusinessPins();
    },
  },
  methods: {
    ...mapActions("organization", ["changeOrganization"]),
    ...mapActions("location", ["updateLocation"]),
    isActivePath(navItem) {
      if (navItem.text === "Activities" && this.isLocationFiltered === false)
        return this.$route.path.toLowerCase().startsWith("/businesses/" + this.organization.id + "/business-todo");
      else if (navItem.text === "Settings")
        return (
          this.$route.path.toLowerCase().startsWith(`/businesses/${this.organization.id}/business`) &&
          !this.$route.path.toLowerCase().startsWith(`/businesses/${this.organization.id}/business-todo`)
        );
      else return this.$route.path.toLowerCase().startsWith(navItem.path.toLowerCase());
    },
    navigateIfNotCurrent(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    getOrganizationIcon(org) {
      switch (org.type) {
        case "predictile":
          return "predictile-color-logo";
        case "business":
          return "business-icon";
        case "user":
          return "user-circle-icon";
      }
    },
    getOrganizationDescriptor(org) {
      switch (org.type) {
        case "predictile":
          return "Predictile";
        case "business":
          return "Business User";
        case "user":
          return this.organization.customerLabelSingular !== null ? capitalize(this.organization.customerLabelSingular) + " Profile" : "Member Profile";
      }
    },
    async updateOrganization(id) {
      await this.changeOrganization(id);
      await this.redirectToHome();
    },
    startTodoInterval() {
      if (this.todoInterval) return;

      this.todoInterval = setInterval(() => {
        this.loadTodoCount();
      }, 90000);
    },
    loadTodoCount() {
      if (!this.userId) return;

      if (this.isLoadingCount === true || !this.organization || this.organization.useTodoSetting === false || this.isLocationFiltered === false) return;

      this.isLoadingCount = true;
      this.$axios
        .get(`/businesses/${this.organization.id}/todo/location-user-assigned/count`)
        .then((res) => {
          this.todoCount = res.data.count;
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 401) {
            console.warn("Skipping todo count — unauthorized");
          } else {
            console.error("Todo count failed", err);
          }
        })
        .finally(() => {
          this.isLoadingCount = false;
        });
    },
    activation() {
      this.$router.push(`/activation/${this.organization.id}`);
    },
    async checkForBusinessPins() {
      try {
        if (!this.isOrganizationBusiness) return;
        const response = await this.$axios.get(`/business/${this.organization.id}/has-pins`);
        this.businessHasPins = response.data.hasPins;
      } catch {
        this.businessHasPins = false;
      }
    },
    async enablePinMode() {
      sessionStorage.removeItem("pinMode");
      sessionStorage.setItem("hasStartedPinMode", true);
      await this.$axios.post(`/businesses/${this.organization.id}/enable-pin-mode`);
      this.$router.push("/pin-mode");
    },
  },
};
